/**
 * ข้อความยืนยันการยุติ/ลบสัญญา — ใช้ร่วมหน้ารายการและหน้ารายละเอียด
 *
 * BUG-S2085-006: หน้ารายการถามแค่ "คุณต้องการยกเลิกสัญญานี้ใช่หรือไม่?" (ไม่มีเลขสัญญา ไม่บอกว่าย้อนไม่ได้
 * ไม่ใช่ปุ่มสีอันตราย) ขณะที่หน้ารายละเอียดบอก — ทั้งที่ผลเหมือนกันทุกประการ: สัญญา → TERMINATED
 * (สถานะปลายทาง) + การเช่าที่ผูกอยู่ถูกยกเลิก + มัดจำที่ชำระแล้วเปลี่ยนเป็น "รอคืนมัดจำ"
 *
 * FB-2026-0006 (แก้ 3 พ.ย. 2026): ข้อความต้องตรงกับที่ backend ทำจริงตาม "ประเภท + สถานะ" ของสัญญา
 * - แนบท้าย **ร่าง** ถูกปฏิเสธ/ลบ → ปลดรายการเช่าออกเท่านั้น การเช่ายังใช้งานต่อ
 *   (`crud/contract.terminate` + `delete_contract` → `release_draft_addendum`) — เดิมกล่องบอก
 *   "การเช่าทั้งหมดจะถูกยกเลิก" ผู้ใช้เลยไม่กล้าปฏิเสธฉบับที่เลือกรายการเช่าผิด
 * - แนบท้าย **มีผลแล้ว** ถูกยกเลิก → cascade เต็มเหมือนสัญญาทั่วไป (การเช่า 1 รายการของแนบท้ายถูกยกเลิก)
 * - สัญญาหลัก (MAIN) ยกเลิกได้ต่อเมื่อไม่มีแนบท้ายที่มีผลค้าง (`_assert_no_active_sub_contracts`)
 */
export type ContractKind = 'MAIN' | 'ADDENDUM' | string | null | undefined;

export const CONTRACT_END_CONSEQUENCES =
  'การเช่าทั้งหมดที่ผูกกับสัญญานี้จะถูกยกเลิก เงินมัดจำที่ชำระแล้วจะเปลี่ยนเป็น "รอคืนมัดจำ" ' +
  'และการกระทำนี้ไม่สามารถเปลี่ยนแปลงได้';

/** ยกเลิกแนบท้ายที่มีผลแล้ว — แนบท้าย 1 ฉบับ = การเช่า 1 รายการ */
export const ADDENDUM_END_CONSEQUENCES =
  'การเช่าที่ผูกกับแนบท้ายนี้จะถูกยกเลิก (ของที่ยังไม่ได้รับคืนจะมีใบตามเก็บ) เงินมัดจำที่ชำระแล้วจะเปลี่ยนเป็น "รอคืนมัดจำ" ' +
  'และการกระทำนี้ไม่สามารถเปลี่ยนแปลงได้';

/** ปฏิเสธ/ลบแนบท้ายที่ยังเป็นร่าง — ไม่ยกเลิกการเช่า */
export const DRAFT_ADDENDUM_RELEASE_CONSEQUENCES =
  'ระบบจะปลดรายการเช่าออกจากแนบท้ายนี้ — การเช่ายังใช้งานต่อตามปกติ (ไม่ถูกยกเลิก) ' +
  'และทำแนบท้ายใหม่ให้รายการเช่านี้ได้';

const label = (contractNumber?: string | null) => (contractNumber ? ` ${contractNumber}` : 'นี้');

export const terminateContractConfirmMessage = (contractNumber?: string | null, contractType?: ContractKind) =>
  contractType === 'ADDENDUM'
    ? `คุณต้องการยกเลิกสัญญาแนบท้าย${label(contractNumber)} ใช่หรือไม่? ${ADDENDUM_END_CONSEQUENCES}`
    : `คุณต้องการยกเลิกสัญญา${label(contractNumber)} ใช่หรือไม่? ${CONTRACT_END_CONSEQUENCES}`;

/** ปฏิเสธได้เฉพาะสัญญาร่าง (backend /approve ตรวจ DRAFT) */
export const rejectContractConfirmMessage = (contractNumber?: string | null, contractType?: ContractKind) =>
  contractType === 'ADDENDUM'
    ? `คุณต้องการปฏิเสธสัญญาแนบท้าย${label(contractNumber)} ใช่หรือไม่? แนบท้ายฉบับนี้จะถูกยกเลิก — ` +
      `${DRAFT_ADDENDUM_RELEASE_CONSEQUENCES} · ย้อนกลับไม่ได้`
    : `คุณต้องการปฏิเสธสัญญา${label(contractNumber)} ใช่หรือไม่? สัญญาจะถูกยกเลิก — ${CONTRACT_END_CONSEQUENCES}`;

/** ลบได้เฉพาะ DRAFT / TERMINATED (เมนู + backend delete_contract) */
export const deleteContractConfirmMessage = (
  contractNumber?: string | null,
  contractType?: ContractKind,
  status?: string | null,
) =>
  contractType === 'ADDENDUM' && status === 'DRAFT'
    ? `คุณต้องการลบสัญญาแนบท้าย${label(contractNumber)} ใช่หรือไม่? ${DRAFT_ADDENDUM_RELEASE_CONSEQUENCES} · ` +
      'การลบไม่สามารถเปลี่ยนแปลงได้'
    : `คุณต้องการลบสัญญา${label(contractNumber)} ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนแปลงได้`;
